// Az `ExerciseCard` komponens egy edzéskártyát jelenít meg, amely tartalmazza az edzés képét, nevét, és célzott izomcsoportokat.
// A kártyára kattintva egy naptáras "Pop-up Window" nyílik meg, ahol az edzés logolható.

import React, { useState } from 'react';
import {
  Card, CardMedia, CardContent, Typography, Box, Chip, Dialog, DialogTitle, DialogContent, Button, TextField
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { Exercise } from '../utils/types';

// Az `ExerciseCardProps` interfész meghatározza a komponens által elvárt `exercise` prop típusát.
interface ExerciseCardProps {
  exercise: Exercise;
}

const ExerciseCard: React.FC<ExerciseCardProps> = ({ exercise }) => {
  // Állapotkezelők a pop-up window megnyitásához és a kiválasztott dátum tárolásához.
  const [open, setOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs());

  // Függvény a kártyára való kattintás kezelésére, megnyitja a párbeszédablakot.
  const handleCardClick = () => {
    setOpen(true);
  };

  // Függvény a párbeszédablak bezárásához.
  const handleClose = () => {
    setOpen(false);
  };

  // Függvény az edzés logolására a kiválasztott dátum alapján.
  const logExercise = () => {
    if (selectedDate) {
      const dateKey = selectedDate.format('YYYY-MM-DD'); // Ez a sor a selectedDate nevű Dayjs objektum formázását végzi el.
      const existingLogs: Exercise[] = JSON.parse(localStorage.getItem(dateKey) || '[]'); // Megpróbálja beolvasni a localStorage-ból a dateKey kulcshoz tartozó adatot, ha nem sikerül, akkor üres tömböt ad vissza.

      // Az edzés objektum hozzáadása a tárolt adatokhoz.
      localStorage.setItem(dateKey, JSON.stringify([...existingLogs, exercise]));

      handleClose(); // A párbeszédablak bezárása.
    }
  };

  // Függvény a naplózott edzések lekérésére a kiválasztott dátumhoz.
  const getLoggedExercises = (): Exercise[] => {
    if (selectedDate) {
      const dateKey = selectedDate.format('YYYY-MM-DD');
      return JSON.parse(localStorage.getItem(dateKey) || '[]');

    }
    // Ha a selectedDate nincs meghatározva, vagy ha a localStorage-ban nincs adat az adott dátumhoz, akkor az üres tömböt adjuk vissza. 
    // Ez biztosítja, hogy a függvény mindig egy Exercise[] típusú tömböt adjon vissza, és elkerüljük a null vagy undefined kezelési problémákat.
    return [];
  };

  return (
    <>
      <Card
        onClick={handleCardClick}
        sx={{
          height: 350,
          transition: 'transform 0.2s',
          '&:hover': { transform: 'scale(1.05)' },
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          cursor: 'pointer',
          justifyContent: 'space-between',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
          borderRadius: 2,
        }}
      >
        {/* Edzés képe megjelenítése */}
        <CardMedia
          component="img"
          image={exercise.gifUrl}
          alt={exercise.name}
          sx={{
            height: 200,
            objectFit: 'contain',
          }}
        />
        <CardContent>
          {/* Edzés neve */}
          {/* A variant attribútum a Material-UI Typography komponensének egyik tulajdonsága.
          Meghatározza, hogy a szöveg milyen stílusú legyen*/}
          <Typography variant="h6" component="div" sx={{ textAlign: 'center', mb: 1 }}>
            {exercise.name}
          </Typography>
          {/* Címkék az edzés testrészéről és célterületéről */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
            {/* A Chip komponens egy kis lekerekített elem, amely egy címkét vagy egy kis információt jelenít meg.*/}
            <Chip label={exercise.bodyPart} variant="outlined" sx={{ color: '#1565c0', borderColor: '#1565c0', borderRadius: '16px', fontSize: '14px' }} />
            <Chip label={exercise.target} variant="outlined" sx={{ color: '#1565c0', borderColor: '#1565c0', borderRadius: '16px', fontSize: '14px' }} />
          </Box>
        </CardContent>
      </Card>

      {/* Pop-up window a dátumválasztáshoz és az edzés logolásához */}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>Log Exercise</DialogTitle>
        <DialogContent sx={{ px: 3, py: 2 }}>
          <Box display="flex" flexDirection="row" alignItems="flex-start" gap={2}>
            {/* Naptár komponens a dátumválasztáshoz */}
            {/*A LocalizationProvider a Material-UI x-date-pickers könyvtárának egy komponense, a dateAdapter={AdapterDayjs} meghatározza, hogy a dátumok kezeléséhez a dayjs könyvtárat használja*/}
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Select Date"
                value={selectedDate}
                onChange={(date) => setSelectedDate(date)}
                sx={{ width: '150px', mt: 1 }}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </LocalizationProvider>
            {/* Lista a naplózott edzésekről a kiválasztott dátumhoz */}
            <Box flex={1}>
              <Typography variant="h6">Logged Exercises</Typography>
              {getLoggedExercises().map((loggedExercise, index) => (
                <Typography key={index}>• {loggedExercise.name}</Typography> // Csak az edzés nevét jeleníti meg.
              ))}
            </Box>
          </Box>
          <Button onClick={logExercise} variant="contained" color="primary" sx={{ mt: 2 }}>
            Log This Exercise
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ExerciseCard;
